import {
  AppShell,
  Center,
  Group,
  Image,
  Text,
  ThemeIcon,
  Tooltip,
  UnstyledButton,
} from '@mantine/core'
import { User } from '@ospace/auth'
import {
  isClientsDistributionPipelineAvailableToUserRole,
  isClientsLeadsDashboardAvailableToUserRole,
  isClientTeamsAvailableToUserRole,
  userTypes,
} from '@ospace/core-auth'
import { isClientsSalesDashboardAvailableToUserRole } from '@ospace/core-auth'
import { getUserNavigationCategory } from '@ospace/home'
import { Client } from '@ospace/schemas'
import {
  IconChartArcs,
  IconChartBar,
  IconChartBubble,
  IconChartHistogram,
  IconChevronLeft,
  IconChevronRight,
  IconHome,
  IconSwitchHorizontal,
  IconUsersGroup,
} from '@tabler/icons-react'
import cx from 'clsx'
import { Link } from 'react-router-dom'

import classes from './SideNavbar.module.css'

export type PageId =
  | 'dashboard'
  | 'leads'
  | 'sales'
  | 'pipeline'
  | 'analytics'
  | 'programs'
  | 'my-teams'

type SideNavbarProps = {
  opened: boolean
  setOpened: any
  client?: Client
  activePageId: PageId
  user?: User
  role: userTypes
}
export const SideNavbar = (props: SideNavbarProps) => {
  const { opened, setOpened, client, activePageId } = props

  const navigationCategory = props.user ? getUserNavigationCategory(props.user, props.role) : null

  const SideBarCollapseButton = (
    <UnstyledButton onClick={() => setOpened((o: any) => !o)}>
      {opened ? (
        <Tooltip label='Collapse' withArrow position='right'>
          <IconChevronLeft stroke={3} className={classes.navIcon} />
        </Tooltip>
      ) : (
        <Tooltip label='Expand' withArrow position='right'>
          <IconChevronRight stroke={3} className={classes.navIcon} />
        </Tooltip>
      )}
    </UnstyledButton>
  )

  const Logo = opened ? (
    <Image className={classes.image} src={'/media/logos/oSpace.svg'} width={200} height={40} />
  ) : (
    <Center w={100}>
      <Image
        className={classes.image_small}
        src={'/media/logos/ospace-xs.svg'}
        width={40}
        height={40}
      />
    </Center>
  )

  return (
    <AppShell.Navbar
      // width={SideNavBarWidth}

      className={classes.sideBar}
    >
      {SideBarCollapseButton}
      <AppShell.Section grow>
        <Group className={classes.header} justify='space-between'>
          {Logo}
        </Group>
        <PageLink
          to={
            client && props.role !== userTypes.CampaignUser ? `/client/${client.id}/dashboard` : '/'
          }
          pageId='dashboard'
          activePageId={activePageId}
          icon={<IconHome />}
          label='Home'
          color='orange'
          sideBarOpened={opened}
          disabled={!client}
        />
        <PageLink
          to={client ? `/client/${client.id}/leads-dashboard` : ''}
          pageId='leads'
          activePageId={activePageId}
          icon={<IconChartHistogram />}
          label='Leads'
          color='green'
          disabled={
            !client ||
            !isClientsLeadsDashboardAvailableToUserRole({
              client: client as Client,
              userRole: props.role,
            })
          }
          sideBarOpened={opened}
        />
        <PageLink
          to={client ? `/client/${client.id}/distribution-pipeline` : ''}
          pageId='pipeline'
          activePageId={activePageId}
          icon={<IconChartBubble />}
          label='Pipeline'
          color='violet'
          disabled={
            !client ||
            !isClientsDistributionPipelineAvailableToUserRole({
              client: client,
              userRole: props.role,
            })
          }
          sideBarOpened={opened}
        />
        <PageLink
          to={client ? `/client/${client.id}/sales-dashboard` : ''}
          pageId='sales'
          activePageId={activePageId}
          icon={<IconChartBar />}
          label='Sales'
          color='grape'
          disabled={
            !client ||
            !isClientsSalesDashboardAvailableToUserRole({
              client: client,
              userRole: props.role,
            })
          }
          sideBarOpened={opened}
        />
        <PageLink
          to={client ? buildProgramsLink(props.role, client) : ''}
          pageId='programs'
          activePageId={activePageId}
          icon={<IconChartArcs />}
          label='Programs'
          color='cyan'
          sideBarOpened={opened}
        />
      </AppShell.Section>
      <AppShell.Section className={classes.footer}>
        {(navigationCategory === 'internal' ||
          navigationCategory === 'external-multilpe-clients') && (
          <Link to='/' className={cx(classes.link)}>
            <ThemeIcon variant='light' className={classes.linkIcon} size={'md'}>
              <IconSwitchHorizontal stroke={1.5} />
            </ThemeIcon>
            {opened && <Text>Change Client</Text>}
          </Link>
        )}

        {client &&
          isClientTeamsAvailableToUserRole({
            userRole: props.role,
            client: client,
          }) && (
            <PageLink
              to={client ? `/client/${client?.id}/my-teams` : ``}
              pageId='my-teams'
              activePageId={activePageId}
              icon={<IconUsersGroup stroke={1.5} />}
              label='My Teams'
              color='cyan'
              sideBarOpened={opened}
            />
          )}
      </AppShell.Section>
    </AppShell.Navbar>
  )
}

const buildProgramsLink = (role: userTypes, client: Client) => {
  if (role === userTypes.CampaignUser) {
    return '/'
  }
  return `/client/view/${client.id}`
}

type PageLinkProps = {
  to: string
  pageId: PageId
  activePageId: PageId
  icon: any
  label: string
  disabled?: boolean
  sideBarOpened?: boolean
  color: string
}

const PageLink = (props: PageLinkProps) => {
  // const { classes, cx } = useStyles()
  if (props.disabled) {
    return (
      <span
        className={cx(classes.link, {
          [classes.linkActive]: props.pageId === props.activePageId,
          [classes.linkDisabled]: props.disabled,
        })}
        key={props.pageId}
        color={'dimmed'}
      >
        {props.sideBarOpened ? (
          <>
            <ThemeIcon
              color={props.disabled ? 'gray' : props.color}
              variant='light'
              className={classes.linkIcon}
              size={'md'}
            >
              {props.icon}
            </ThemeIcon>

            <Text size='md'>{props.label}</Text>
          </>
        ) : (
          <Tooltip label={props.label} withArrow position='right'>
            <ThemeIcon color={'gray'} variant='light' className={classes.linkIcon} size={'lg'}>
              {props.icon}
            </ThemeIcon>
          </Tooltip>
        )}
      </span>
    )
  }
  return (
    <Link
      className={cx(classes.link, {
        [classes.linkActive]: props.pageId === props.activePageId,
        [classes.linkDisabled]: props.disabled,
      })}
      to={props.to}
      key={props.pageId}
      color={'dark'}
      role='link'
      aria-label={props.label}
    >
      {props.sideBarOpened ? (
        <>
          <ThemeIcon
            color={props.disabled ? 'gray' : props.color}
            variant='light'
            className={classes.linkIcon}
            size={'md'}
          >
            {props.icon}
          </ThemeIcon>

          <Text size='md' fw={props.pageId === props.activePageId ? 700 : undefined}>
            {props.label}
          </Text>
        </>
      ) : (
        <Tooltip label={props.label} withArrow position='right'>
          <ThemeIcon color={props.color} variant='light' className={classes.linkIcon} size={'md'}>
            {props.icon}
          </ThemeIcon>
        </Tooltip>
      )}
    </Link>
  )
}
